import { MouseEvent, useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { Person } from '@cvut/profit-api-types/lib/theses'

import Card from './Card'
import PersonCard from './PersonCard'
import { useLocale } from '../locale'
import { usePeople } from '../api/people'
import { formatPersonFullName } from '../utils/person'
import { typingDelay } from '../config'
import * as style from '../features/thesis/ThesisReviewerSearchCard.style'


interface OptionProps {
  person: Person
  onClick: () => void
}

const PersonOption = ({ person, onClick }: OptionProps): JSX.Element => {
  const handleClick = (evt: MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault()
    onClick()
  }

  return (
    <li>
      <a href='#' className={style.link} onClick={handleClick}>
        <PersonCard name={formatPersonFullName(person)} contact={person.email} />
      </a>
    </li>
  )
}

interface PanelProps {
  message: string | null
  people: Person[]
  onPersonSelect: (person: Person) => unknown
}

const SearchSuggestionsPanel = ({ message, people, onPersonSelect }: PanelProps): JSX.Element | null => {
  if (!message && people.length === 0) {
    return null
  }

  return (
    <div className={style.searchContent}>
      {message && (
        <div className={style.message}>{message}</div>
      )}
      {people.length > 0 && (
        <ul className={style.searchResultsList}>
          {people.map(person => (
            <PersonOption key={person.id ?? person.username} person={person} onClick={() => onPersonSelect(person)} />
          ))}
        </ul>
      )}
    </div>
  )
}

interface Props {
  title?: string
  placeholder?: string
  onPersonSelect: (person: Person) => unknown
  excludeUsernames?: string[]
  showAllPeopleInitially?: boolean
}

const PeopleSearchCard = ({
  title,
  placeholder,
  onPersonSelect,
  excludeUsernames = [],
  showAllPeopleInitially = true,
}: Props): JSX.Element => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [people, setPeople] = useState<Person[]>([])
  const [showList, setShowList] = useState(false)
  const [userDismissed, setUserDismissed] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [peopleStatus, getPeople] = usePeople()
  const { l } = useLocale()

  const getPeopleDebounced = _.debounce(async () => {
    if (inputValue) {
      await getPeople(inputValue, undefined, 50) // Search with query
    } else if (showAllPeopleInitially && hasInteracted) {
      await getPeople(undefined, undefined, 1000) // Get all people when no search query
    }
  }, typingDelay)

  useEffect(() => {
    // Only fetch and show if user has interacted with the input
    if (hasInteracted && (inputValue || showAllPeopleInitially)) {
      void getPeopleDebounced()
      // Only show list if user hasn't manually dismissed it
      if (!userDismissed) {
        setShowList(true)
      }
    } else if (!hasInteracted) {
      setPeople([])
      setShowList(false)
    } else if (!inputValue && !showAllPeopleInitially) {
      setPeople([])
      setShowList(false)
    }

    return getPeopleDebounced.cancel
  }, [inputValue, showAllPeopleInitially, userDismissed, hasInteracted])

  useEffect(() => {
    if (peopleStatus.state === 'success') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setPeople(peopleStatus.data!.data)
    }
  }, [peopleStatus.state])

  const handlePersonSelect = (person: Person) => {
    setInputValue('')
    setShowList(false)
    setPeople([])
    setUserDismissed(true)
    // Blur the input to prevent re-focus from showing the list
    inputRef.current?.blur()
    onPersonSelect(person)
  }

  let message = null
  if (showList && (inputValue || showAllPeopleInitially)) {
    if (peopleStatus.state === 'loading') {
      message = l.personSearchBox.loading
    } else if (peopleStatus.state === 'error') {
      message = l.personSearchBox.error
    } else if (peopleStatus.state === 'success' && peopleStatus.data?.data.length === 0) {
      message = l.personSearchBox.noResults
    }
  }

  const filteredPeople = people.filter(person => {
    return !excludeUsernames.includes(person.username)
  })

  const shouldShowPanel = showList && (inputValue || showAllPeopleInitially)

  return (
    <Card>
      <Card.Header title={title ?? 'Search People'} />
      <Card.Content>
        <div className={style.searchWrapper}>
          <span className={style.searchInputButton} />
          <div className={style.searchInputWrapper}>
            <input
              ref={inputRef}
              type='search'
              className={style.searchInput}
              placeholder={placeholder ?? 'Search for a person...'}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onFocus={() => {
                setHasInteracted(true)
                if (inputValue || showAllPeopleInitially) {
                  setUserDismissed(false)
                  setShowList(true)
                }
              }}
            />
            {shouldShowPanel && (
              <SearchSuggestionsPanel
                message={message}
                people={filteredPeople}
                onPersonSelect={handlePersonSelect}
              />
            )}
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}

export default PeopleSearchCard
